import React, { useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { formatCurrency } from '../lib/utils';
import { Loader2, RefreshCw, AlertCircle, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BulkPriceUpdateDialog({ open, onOpenChange, customer, onApplied }) {
  const { language } = useLanguage();
  const today = new Date().toISOString().split('T')[0];
  const firstOfMonth = `${today.slice(0, 8)}01`;

  const [startDate, setStartDate] = useState(firstOfMonth);
  const [endDate, setEndDate] = useState(today);
  const [items, setItems] = useState([]); // [{product_name, current_prices:[], new_price, line_count, total_quantity}]
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null); // {summary, affected}
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const T = (ar, en) => (language === 'ar' ? ar : en);

  const reset = () => {
    setItems([]);
    setPreview(null);
    setError('');
    setDone(false);
  };

  const handleOpenChange = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const loadItems = async () => {
    if (!customer?.id || !startDate || !endDate) return;
    setLoading(true);
    setError('');
    setPreview(null);
    setDone(false);
    try {
      const res = await axios.get(`${API}/sales/customer-items`, {
        params: { customer_id: customer.id, start_date: startDate, end_date: endDate },
      });
      const loaded = (res.data.items || []).map((it) => ({
        ...it,
        new_price: '',
      }));
      setItems(loaded);
      if (loaded.length === 0) {
        setError(T('لا توجد عناصر في فواتير هذا العميل ضمن النطاق المحدد.', 'No items found in this customer\'s sales for the chosen range.'));
      }
    } catch (e) {
      setError(T('فشل تحميل العناصر.', 'Failed to load items.'));
    } finally {
      setLoading(false);
    }
  };

  const buildPayload = (dryRun) => {
    const price_updates = items
      .filter((it) => it.new_price !== '' && !Number.isNaN(parseFloat(it.new_price)))
      .map((it) => ({ product_name: it.product_name, new_price: parseFloat(it.new_price) }));
    return {
      customer_id: customer.id,
      start_date: startDate,
      end_date: endDate,
      price_updates,
      dry_run: dryRun,
    };
  };

  const runPreview = async () => {
    const payload = buildPayload(true);
    if (payload.price_updates.length === 0) {
      setError(T('أدخل سعراً جديداً على الأقل لعنصر واحد.', 'Enter a new price for at least one item.'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/sales/bulk-update-prices`, payload);
      setPreview(res.data);
      setDone(false);
    } catch (e) {
      setError(e?.response?.data?.detail || T('فشلت المعاينة.', 'Preview failed.'));
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    if (!preview) return;
    setApplying(true);
    setError('');
    try {
      const res = await axios.post(`${API}/sales/bulk-update-prices`, buildPayload(false));
      setPreview(res.data);
      setDone(true);
      if (onApplied) onApplied(res.data.summary);
    } catch (e) {
      setError(e?.response?.data?.detail || T('فشل التطبيق.', 'Apply failed.'));
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="bulk-price-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            {T('تصحيح الأسعار الجماعي', 'Bulk Price Adjustment')}
            {customer && (
              <span className="ms-2 text-base text-muted-foreground">— {customer.name}</span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Date range + load */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
            <div>
              <Label htmlFor="bulk-start">{T('من تاريخ', 'Start date')}</Label>
              <Input
                id="bulk-start"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="bulk-start-date"
              />
            </div>
            <div>
              <Label htmlFor="bulk-end">{T('إلى تاريخ', 'End date')}</Label>
              <Input
                id="bulk-end"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="bulk-end-date"
              />
            </div>
            <Button onClick={loadItems} disabled={loading} data-testid="bulk-load-items">
              {loading ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : <RefreshCw className="me-2 h-4 w-4" />}
              {T('تحميل العناصر', 'Load items')}
            </Button>
          </div>

          {/* Items table */}
          {items.length > 0 && (
            <div className="border border-border/40 rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{T('المنتج', 'Item')}</TableHead>
                    <TableHead>{T('السعر الحالي', 'Current price(s)')}</TableHead>
                    <TableHead>{T('عدد المرات', 'Lines')}</TableHead>
                    <TableHead>{T('إجمالي الكمية', 'Total qty')}</TableHead>
                    <TableHead className="w-40">{T('السعر الجديد', 'New price')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((it, idx) => (
                    <TableRow key={it.product_name} data-testid={`bulk-item-row-${idx}`}>
                      <TableCell className="font-medium">{it.product_name}</TableCell>
                      <TableCell dir="ltr" className="text-muted-foreground">
                        {it.current_prices.map((p) => formatCurrency(p)).join(', ')}
                      </TableCell>
                      <TableCell>{it.line_count}</TableCell>
                      <TableCell>{it.total_quantity}</TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          inputMode="decimal"
                          placeholder={T('اتركه فارغاً للتجاهل', 'Leave empty to skip')}
                          value={it.new_price}
                          onChange={(e) => {
                            const v = e.target.value;
                            setItems((prev) => prev.map((x, i) => (i === idx ? { ...x, new_price: v } : x)));
                          }}
                          data-testid={`bulk-new-price-${idx}`}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Preview summary */}
          {preview && (
            <div className={`rounded-lg p-4 border ${done ? 'border-green-300 bg-green-50' : 'border-blue-300 bg-blue-50'}`} data-testid="bulk-preview-summary">
              <div className="flex items-start gap-2">
                {done ? <CheckCircle2 className="text-green-600 mt-0.5" size={18} /> : <AlertCircle className="text-blue-600 mt-0.5" size={18} />}
                <div className="flex-1">
                  <p className="font-semibold mb-2">
                    {done
                      ? T('تم تطبيق التغييرات بنجاح', 'Changes applied successfully')
                      : T('معاينة قبل التطبيق', 'Preview — not yet applied')}
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div>
                      <div className="text-muted-foreground">{T('فواتير ضمن النطاق', 'Sales in range')}</div>
                      <div className="font-bold">{preview.summary.sales_in_range}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">{T('فواتير ستتأثر', 'Affected invoices')}</div>
                      <div className="font-bold">{preview.summary.affected_count}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">{T('فرق الإجمالي', 'Total difference')}</div>
                      <div className={`font-bold ${preview.summary.difference >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {preview.summary.difference >= 0 ? '+' : ''}{formatCurrency(preview.summary.difference)}
                      </div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">{T('تأثير الديون', 'Debt change')}</div>
                      <div className={`font-bold ${preview.summary.debt_delta > 0 ? 'text-red-600' : preview.summary.debt_delta < 0 ? 'text-green-600' : ''}`}>
                        {preview.summary.debt_delta >= 0 ? '+' : ''}{formatCurrency(preview.summary.debt_delta)}
                      </div>
                    </div>
                  </div>

                  {preview.affected.length > 0 && (
                    <div className="mt-4 max-h-64 overflow-y-auto border border-border/40 rounded">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{T('رقم الفاتورة', 'Invoice #')}</TableHead>
                            <TableHead>{T('التاريخ', 'Date')}</TableHead>
                            <TableHead>{T('الإجمالي القديم', 'Old total')}</TableHead>
                            <TableHead>{T('الإجمالي الجديد', 'New total')}</TableHead>
                            <TableHead>{T('الحالة', 'Status')}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {preview.affected.map((a) => (
                            <TableRow key={a.sale_id}>
                              <TableCell className="font-mono text-xs">{a.invoice_number}</TableCell>
                              <TableCell>{a.date}</TableCell>
                              <TableCell className="text-muted-foreground">{formatCurrency(a.old_total)}</TableCell>
                              <TableCell className="font-bold">{formatCurrency(a.new_total)}</TableCell>
                              <TableCell className="text-xs">
                                {a.old_status === a.new_status ? a.new_status : `${a.old_status} → ${a.new_status}`}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="text-red-600 text-sm flex items-center gap-2" data-testid="bulk-error">
              <AlertCircle size={16} /> {error}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => handleOpenChange(false)} data-testid="bulk-close">
            {done ? T('إغلاق', 'Close') : T('إلغاء', 'Cancel')}
          </Button>
          {items.length > 0 && !done && (
            <Button onClick={runPreview} variant="secondary" disabled={loading || applying} data-testid="bulk-preview-btn">
              {loading && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
              {T('معاينة', 'Preview')}
            </Button>
          )}
          {preview && !done && preview.summary.affected_count > 0 && (
            <Button onClick={apply} disabled={applying} data-testid="bulk-apply-btn">
              {applying && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
              {T('تأكيد التطبيق', 'Confirm & Apply')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
